import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useAuthenticateAdminWithSocial } from "@/api/auth";
import { setApiAccessToken, setToken, setUserId } from "@/helpers/localStorage";

const RETRY_INTERVAL = 3000;

const AdminModalPage = () => {
  const navigate = useNavigate();
  const { web3Auth, isConnected } = useWeb3Auth();
  const { mutateAsync: mutateSocial, isError } =
    useAuthenticateAdminWithSocial();

  const retryingRef = useRef(false);

  const tryLogin = async () => {
    if (retryingRef.current || !web3Auth) return;

    retryingRef.current = true;

    try {
      await web3Auth.connect();

      const { typeOfLogin } = await web3Auth.getUserInfo();
      const { idToken } = await web3Auth.authenticateUser();
      if (!idToken) throw new Error("No idToken received from web3auth");

      setToken(idToken);

      if (typeOfLogin) {
        const result = await mutateSocial({ token: idToken });
        setApiAccessToken(result.accessToken);

        const { email } = await web3Auth.getUserInfo();
        setUserId(email || "");

        navigate("/admin/wallet-screener");
      }
    } catch (err) {
      console.error("Auto login failed:", err);
      setApiAccessToken("");
      localStorage.clear();
      navigate("/admin");
    } finally {
      retryingRef.current = false;
    }
  };

  // if user closed web3auth modal without logging in show modal periodically
  useEffect(() => {
    if (!web3Auth) {
      return;
    }

    if (web3Auth?.status === "ready") {
      tryLogin();
    }

    const interval = setInterval(() => {
      if (!isConnected && web3Auth.status === "ready") {
        tryLogin();
      }
    }, RETRY_INTERVAL);

    if (isConnected) {
      navigate("/admin/wallet-screener", { replace: true });
    }

    return () => clearInterval(interval);
  }, [web3Auth, isConnected, navigate, mutateSocial]);

  if (isConnected) {
    return null;
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center w-full h-full">
        <p className="text-red-500">Authentication failed</p>
      </div>
    );
  }

  if (!isConnected && web3Auth?.status === "ready") {
    return (
      <div className="flex flex-1 items-center justify-center w-full h-full">
        <p>Connecting to Web3Auth...</p>
      </div>
    );
  }
};

export default AdminModalPage;
